import { defineComponent as _defineComponent } from 'vue'
import { MonetizationType, UpcomingReleaseLabel } from '@/@types/graphql-types';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { formattedReleaseDate } from '@/helpers/date-helper';
import { useLanguageStore } from '@/helpers/composables/useStores';
import { BuyBoxFilterOptions } from '@/stores/user.store';
import { SnowplowContext } from '@/helpers/tracking/providers';
import BuyBoxUpcomingOfferNew from './BuyBoxUpcomingOfferNew.vue';
import { AssertByTypename } from '@/helpers/graphql-model-helper';
import { GetNodeTitleDetailsQuery } from '@/pages/graphql/queries/GetNodeTitleDetails.query';
import { computed } from 'vue';
import { useI18n } from '@/helpers/composables/useI18n';

export type UpcomingRelease = AssertByTypename<
	GetNodeTitleDetailsQuery['node'],
	'Movie' | 'Show' | 'Season'
>['content']['upcomingReleases'][number];

export type UpcomingReleaseBuybox = {
	offer: UpcomingRelease;
	isInGroup: boolean;
	isLastOfGroup: boolean;
	isFirst: boolean;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'BuyBoxUpcomingNew',
  props: {
    upcomingRelease: null,
    title: null,
    isTitleInWatchlist: { type: Boolean },
    isPromotedBuyBox: { type: Boolean },
    filter: null,
    additionalContexts: null
  },
  emits: ["onNotifyMe"],
  setup(__props: any) {

const props = __props as {
	upcomingRelease: UpcomingReleaseBuybox;
	title: TitleDetail;
	isTitleInWatchlist?: boolean;
	isPromotedBuyBox?: boolean;
	filter: BuyBoxFilterOptions;
	additionalContexts: SnowplowContext[];
};

const { language, country } = useLanguageStore();

const { t, tc } = useI18n();





const monetizationType = computed(() => {
	return props.upcomingRelease.offer.package?.monetizationTypes[0];
});

const showOffer = computed(() => {
	if (props.filter === BuyBoxFilterOptions.ALL) return true;
	if (monetizationType.value === MonetizationType.Cinema || isTheatrical.value)
		return props.filter === BuyBoxFilterOptions.CINEMA;
	if (monetizationType.value === MonetizationType.Flatrate || props.upcomingRelease.offer.package?.planOffers.length)
		return props.filter === BuyBoxFilterOptions.SUBSCRIPTION;
	if (['FREE', 'ADS', 'FAST'].includes(monetizationType.value || '')) return props.filter === BuyBoxFilterOptions.BUY;
	if (monetizationType.value === MonetizationType.Rent) return props.filter === BuyBoxFilterOptions.RENT;
	if (monetizationType.value === MonetizationType.Fast) return props.filter === BuyBoxFilterOptions.FAST_TV;

	return false;
});

const isTheatrical = computed(() => props.upcomingRelease.offer.releaseType === 'THEATRICAL');

const releaseDateFormatted = computed(
	() =>
		formattedReleaseDate(
			props.upcomingRelease.offer.releaseDate,
			props.upcomingRelease.offer.label,
			language.value,
			'short',
			country.value
		).heading
);

const providerClearName = computed(() => {
	return props.upcomingRelease.offer.package?.packageId === 350
		? 'Apple TV+'
		: props.upcomingRelease.offer.package?.clearName;
});
const upcomingOfferLabel = computed(() => props.upcomingRelease.offer.label);
const isSeason = computed(() => {
	return [
		UpcomingReleaseLabel.Spring,
		UpcomingReleaseLabel.Summer,
		UpcomingReleaseLabel.Autumn,
		UpcomingReleaseLabel.Winter,
	].includes(upcomingOfferLabel.value);
});
const isDate = computed(() => upcomingOfferLabel.value === UpcomingReleaseLabel.Date);
const isMonth = computed(() => upcomingOfferLabel.value === UpcomingReleaseLabel.Month);
const isYear = computed(() => upcomingOfferLabel.value === UpcomingReleaseLabel.Year);
const isTBA = computed(() => upcomingOfferLabel.value === UpcomingReleaseLabel.ToBeAnnounced);
const isTomorrow = computed(() => upcomingOfferLabel.value === UpcomingReleaseLabel.Tomorrow);
const isToday = computed(() => upcomingOfferLabel.value === UpcomingReleaseLabel.Today);

const upcomingBanner = computed(() => {
	if (
		isTheatrical.value
	) {
		if (isDate.value) return t('WEBAPP_COMING_TO_THEATERS_ON_DATE', { date: releaseDateFormatted.value });
		if (isMonth.value) return t('WEBAPP_COMING_TO_THEATERS_MONTH', { month: releaseDateFormatted.value });
		if (isSeason.value) return t('WEBAPP_COMING_TO_THEATERS_SEASON', { season: releaseDateFormatted.value });
		if (isYear.value) return t('WEBAPP_COMING_TO_THEATERS_YEAR', { year: releaseDateFormatted.value });
		if (isToday.value) return t('WEBAPP_COMING_TO_THEATRES_TODAY');
		if (isTomorrow.value) return t('WEBAPP_COMING_TO_THEATRES_TOMORROW');
		if (isTBA.value) return tc('WEBAPP_COMING_TO_THEATERS_TBA');
	}

	// prettier-ignore
	else if (!props.upcomingRelease.isInGroup && providerClearName.value) {
		// prettier-ignore
		if (isDate.value) return t('WEBAPP_COMING_TO_PROVIDERS_ON_DATE', { provider: providerClearName.value, date: releaseDateFormatted.value });
		// prettier-ignore
		if (isMonth.value) return t('WEBAPP_COMING_TO_PROVIDER_MONTH', { provider: providerClearName.value, month: releaseDateFormatted.value });
		// prettier-ignore
		if (isSeason.value) return t('WEBAPP_COMING_TO_PROVIDER_SEASON', { provider: providerClearName.value, season: releaseDateFormatted.value });
		// prettier-ignore
		if (isYear.value) return t('WEBAPP_COMING_TO_PROVIDER_YEAR', { provider: providerClearName.value, year: releaseDateFormatted.value });
		if (isToday.value) return t('WEBAPP_COMING_TO_PROVIDER_TODAY', { provider: providerClearName.value });
		if (isTomorrow.value) return t('WEBAPP_COMING_TO_PROVIDER_TOMORROW', { provider: providerClearName.value });
		if (isTBA.value) return tc('WEBAPP_COMING_TO_PROVIDER_TBA');
	}

	if (isDate.value) return t('WEBAPP_COMING_TO_STREAMING_ON_DATE', { date: releaseDateFormatted.value });
	if (isMonth.value) return t('WEBAPP_COMING_TO_STREAMING_MONTH', { month: releaseDateFormatted.value });
	if (isSeason.value) return t('WEBAPP_COMING_TO_STREAMING_SEASON', { season: releaseDateFormatted.value });
	if (isYear.value) return t('WEBAPP_COMING_TO_STREAMING_YEAR', { year: releaseDateFormatted.value });
	if (isToday.value) return t('WEBAPP_COMING_TO_STREAMING_TODAY', { day: releaseDateFormatted.value });
	if (isTomorrow.value) return t('WEBAPP_COMING_TO_STREAMING_TOMORROW', { day: releaseDateFormatted.value });
	if (isTBA.value) return tc('WEBAPP_COMING_TO_STREAMING_TBA');

	return t('WEBAPP_COMING_TO_STREAMING_ON_DATE', { date: releaseDateFormatted.value });
});

return { __sfc: true,language, country, t, tc, props, monetizationType, showOffer, isTheatrical, releaseDateFormatted, providerClearName, upcomingOfferLabel, isSeason, isDate, isMonth, isYear, isTBA, isTomorrow, isToday, upcomingBanner, BuyBoxUpcomingOfferNew }
}

})